import React, {useMemo} from 'react'

import type {PublicUser} from '../types/public-user'

interface CurrentUserContextType {
  currentUser: PublicUser | undefined
  currentUserIsSessionOwner: boolean
  isSingleSessionView: boolean
  optedInToFeedback: boolean
}

const CurrentUserContext = React.createContext<CurrentUserContextType | undefined>(undefined)

interface CurrentUserContextProviderProps {
  children: React.ReactNode
  currentUser: PublicUser | undefined
  currentUserIsSessionOwner: boolean
  optedInToFeedback?: boolean
}

export function CurrentUserProvider({
  currentUser,
  currentUserIsSessionOwner,
  optedInToFeedback = false,
  children,
}: CurrentUserContextProviderProps) {
  // A gentle hack. You can only query multiple sessions at once if you're the session owner.
  // So if the current user is not the session owner, we're in a single session view.
  const isSingleSessionView = !currentUserIsSessionOwner
  const value = useMemo(
    () => ({currentUser, currentUserIsSessionOwner, isSingleSessionView, optedInToFeedback}),
    [currentUser, currentUserIsSessionOwner, isSingleSessionView, optedInToFeedback],
  )
  return <CurrentUserContext value={value}> {children} </CurrentUserContext>
}

export function useCurrentUser() {
  const context = React.use(CurrentUserContext)
  if (!context) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider')
  }
  return context
}
